import React, { useState, useEffect } from "react";
import { collection, doc, getDocs, setDoc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useTranslation } from "react-i18next";

export default function NutritionAssessmentTab({ athleteId }) {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    height: "",
    weight: "",
    bodyFat: "",
    allergies: "",
    supplements: "",
    cultural: "",
    nutritionGoals: "",
    dailyIntake: "",
    training: "",
    stress: "",
    sleep: "",
    hydration: ""
  });
  const [isEditing, setIsEditing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    const fetchAssessment = async () => {
      if (!athleteId) return;

      const ref = collection(db, "nutritionAssessments", athleteId, "assessment");
      const snapshot = await getDocs(ref);

      if (!snapshot.empty) {
        const data = snapshot.docs[0].data();
        setForm({
          height: data.height || "",
          weight: data.weight || "",
          bodyFat: data.bodyFat || "",
          allergies: data.allergies || "",
          supplements: data.supplements || "",
          cultural: data.cultural || "",
          nutritionGoals: data.nutritionGoals || "",
          dailyIntake: data.dailyIntake || "",
          training: data.training || "",
          stress: data.stress || "",
          sleep: data.sleep || "",
          hydration: data.hydration || ""
        });
        setLastUpdated(data.createdAt || null);
      } else {
        setIsEditing(true); // No data yet, allow editing
      }
    };

    fetchAssessment();
  }, [athleteId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    const docRef = doc(db, "nutritionAssessments", athleteId, "assessment", "initial");
    const payload = { ...form, createdAt: new Date().toISOString() };
    await setDoc(docRef, payload);
    setLastUpdated(payload.createdAt);
    setIsEditing(false);
  };

  const inputStyle = "w-full px-4 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-[#b22222]";
  const readOnlyStyle = "bg-gray-100 text-gray-600";

  return (
    <div className="bg-white p-4 rounded shadow" dir={i18n.language === "ar" ? "rtl" : "ltr"}>
      <h3 className="text-xl font-semibold mb-4">{t("nutrition_assessment")}</h3>

      {lastUpdated && !isEditing && (
        <p className="text-sm text-gray-500 mb-4">
          {t("last_updated")}: {new Date(lastUpdated).toLocaleDateString()}
        </p>
      )}

      <form className="space-y-6">
        <div>
          <h4 className="font-semibold mb-2">{t("section_basic_info")}</h4>
          <div className="grid sm:grid-cols-3 gap-4">
            {["height", "weight", "bodyFat"].map((key) => (
              <div key={key}>
                <label className="block mb-1">
                  {t(key)} {key === "height" ? "(cm)" : key === "weight" ? "(kg)" : ""}
                </label>
                <input
                  type="text"
                  name={key}
                  value={form[key]}
                  onChange={handleChange}
                  className={`${inputStyle} ${!isEditing ? readOnlyStyle : ""}`}
                  disabled={!isEditing}
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-semibold mb-2">{t("section_dietary")}</h4>
          {["allergies", "supplements", "cultural", "nutritionGoals", "dailyIntake"].map((key) => (
            <div key={key}>
              <label className="block mb-1">{t(key)}</label>
              <textarea
                name={key}
                value={form[key]}
                onChange={handleChange}
                rows={2}
                className={`${inputStyle} ${!isEditing ? readOnlyStyle : ""}`}
                disabled={!isEditing}
              />
            </div>
          ))}
        </div>

        <div>
          <h4 className="font-semibold mb-2">{t("section_lifestyle")}</h4>
          {["training", "stressLevel", "sleep", "hydration"].map((key) => (
            <div key={key}>
              <label className="block mb-1">{t(key)}</label>
              <textarea
                name={key}
                value={form[key]}
                onChange={handleChange}
                rows={2}
                className={`${inputStyle} ${!isEditing ? readOnlyStyle : ""}`}
                disabled={!isEditing}
              />
            </div>
          ))}
        </div>

        <div className="mt-4 flex gap-4">
          {isEditing ? (
            <button
              type="button"
              onClick={handleSave}
              className="bg-[#b22222] text-white px-6 py-2 rounded hover:bg-red-800 transition"
            >
              {t("save")}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="bg-gray-100 text-gray-800 px-6 py-2 rounded hover:bg-gray-200 transition"
            >
              {t("edit")}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
